import { useCallback, useEffect, useRef, useState } from 'react';
import { GridRowId, GridRowModes, GridRowModesModel } from '@mui/x-data-grid-premium';

import { TaskApi } from '../../../../../../../../../api';
import NotificationManager from '../../../../../../../../../common/notifications/NotificationManager';

import { COMMENT_TEXT_FIELD, MAX_COMMENT_LENGTH } from './taskCommentConstants';
import { TaskCommentApiResponse, TaskCommentRow, UseTaskCommentsModalParams } from './taskCommentTypes';

export default function useTaskCommentsModal({
  initialRows,
  taskId,
  ssuId,
  modalBox,
  onCommentsChanged,
  userFirstName,
  userLastName,
  initWithDraftComment = false
}: UseTaskCommentsModalParams) {
  const [rows, setRows] = useState<TaskCommentRow[]>(initialRows);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [isSaving, setIsSaving] = useState(false);
  const [currentCommentText, setCurrentCommentText] = useState('');
  const nextNewRowIdRef = useRef(0);
  const isSavingRef = useRef(false);
  const isMountedRef = useRef(true);
  const saveIntentRowIdRef = useRef<GridRowId | null>(null);
  const didInitDraftRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleAddRow = useCallback(() => {
    const existingNewRow = rows.find(row => row.isNew);

    if (existingNewRow) {
      setRowModesModel(prev => ({
        ...prev,
        [existingNewRow.id]: { mode: GridRowModes.Edit, fieldToFocus: COMMENT_TEXT_FIELD }
      }));
      return;
    }

    const id = createNewRowId(nextNewRowIdRef);
    const newRow: TaskCommentRow = {
      id,
      isNew: true,
      creationDate: new Date().toISOString(),
      createdByFirstName: userFirstName,
      createdByLastName: userLastName,
      commentText: ''
    };

    setRows(prev => [newRow, ...prev]);
    setCurrentCommentText('');
    setRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: COMMENT_TEXT_FIELD }
    }));
  }, [rows, userFirstName, userLastName]);

  useEffect(() => {
    if (!initWithDraftComment || didInitDraftRef.current) {
      return;
    }

    didInitDraftRef.current = true;
    handleAddRow();
  }, [initWithDraftComment, handleAddRow]);

  const markSaveIntent = useCallback(
    (id: GridRowId) => {
      if (!isSaving) {
        saveIntentRowIdRef.current = id;
      }
    },
    [isSaving]
  );

  const handleSaveClick = useCallback(
    (id: GridRowId) => () => {
      if (isSaving) return;
      markSaveIntent(id);
      setRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
    },
    [isSaving, markSaveIntent]
  );

  const handleCancelClick = useCallback(
    (id: GridRowId) => () => {
      saveIntentRowIdRef.current = null;
      setCurrentCommentText('');
      setRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
      setRows(prev => prev.filter(row => row.id !== id || !row.isNew));
    },
    []
  );

  const handleClose = useCallback(() => {
    saveIntentRowIdRef.current = null;
    setCurrentCommentText('');
    setRows(prev => prev.filter(row => !row.isNew));
    setRowModesModel({});
    modalBox.close();
  }, [modalBox]);

  const processRowUpdate = useCallback(
    async (newRow: TaskCommentRow, oldRow: TaskCommentRow) => {
      if (!newRow.isNew || saveIntentRowIdRef.current !== newRow.id || isSavingRef.current) {
        return oldRow;
      }

      if (!isValidCommentText(newRow.commentText)) {
        saveIntentRowIdRef.current = null;
        return oldRow;
      }

      isSavingRef.current = true;
      setIsSaving(true);

      try {
        const trimmedComment = newRow.commentText.trim();
        const response = await TaskApi.addComment(taskId, ssuId, trimmedComment);
        const createdRow = createSavedRow(response?.data ?? null, newRow, trimmedComment, userFirstName, userLastName);

        if (isMountedRef.current) {
          setCurrentCommentText('');
          setRows(prev => prev.map(row => (row.id === newRow.id ? createdRow : row)));
        }
        onCommentsChanged?.();
        NotificationManager.success('Comment added successfully!');

        return createdRow;
      } catch {
        const draftRow = { ...newRow, isNew: true };
        if (isMountedRef.current) {
          setRows(prev => prev.map(row => (row.id === newRow.id ? draftRow : row)));
          setRowModesModel(prev => ({
            ...prev,
            [newRow.id]: { mode: GridRowModes.Edit }
          }));
        }
        NotificationManager.error('Failed to add comment. Please try again.');
        return oldRow;
      } finally {
        saveIntentRowIdRef.current = null;
        isSavingRef.current = false;
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [onCommentsChanged, ssuId, taskId, userFirstName, userLastName]
  );

  return {
    rows,
    rowModesModel,
    setRowModesModel,
    isSaving,
    currentCommentText,
    setCurrentCommentText,
    handleAddRow,
    markSaveIntent,
    handleSaveClick,
    handleCancelClick,
    handleClose,
    processRowUpdate
  };
}

function createSavedRow(
  savedComment: TaskCommentApiResponse | null,
  draftRow: TaskCommentRow,
  commentText: string,
  userFirstName: string,
  userLastName: string
): TaskCommentRow {
  return {
    id: savedComment?.id ?? draftRow.id,
    creationDate: savedComment?.creationDate ?? draftRow.creationDate ?? new Date().toISOString(),
    createdByFirstName: userFirstName,
    createdByLastName: userLastName,
    commentText: savedComment?.commentText ?? commentText,
    isNew: false
  };
}

function createNewRowId(nextNewRowIdRef: { current: number }) {
  nextNewRowIdRef.current += 1;
  return `new-comment-${Date.now()}-${nextNewRowIdRef.current}`;
}

function isValidCommentText(value: string) {
  return value.trim().length > 0 && value.length <= MAX_COMMENT_LENGTH;
}
